import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Formatea una fecha ISO como tiempo relativo ("hace 5 minutos") usando
 * `Intl.RelativeTimeFormat` en el idioma activo de Transloco.
 */
@Pipe({
  name: 'relativeTime',
  standalone: true,
  pure: false,
})
export class RelativeTimePipe implements PipeTransform {
  private readonly transloco = inject(TranslocoService);

  private static readonly UNITS: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  /** Coincide con un offset explícito ('Z' o '+hh:mm'/'-hh:mm') al final del string. */
  private static readonly TIMEZONE_PATTERN = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

  transform(value: string | Date | null | undefined): string {
    if (!value) return '';

    const date = typeof value === 'string' ? this.parseDate(value) : value;
    if (Number.isNaN(date.getTime())) return '';

    const rtf = new Intl.RelativeTimeFormat(this.transloco.getActiveLang(), {
      numeric: 'auto',
    });

    let duration = (date.getTime() - Date.now()) / 1000;

    for (const [amount, unit] of RelativeTimePipe.UNITS) {
      if (Math.abs(duration) < amount) {
        return rtf.format(Math.round(duration), unit);
      }
      duration /= amount;
    }

    return rtf.format(Math.round(duration), 'year');
  }

  /**
   * El backend serializa `LocalDateTime` sin offset de zona (siempre en UTC).
   * Si el string no trae un offset explícito, se le añade 'Z' para que
   * `Date` lo interprete como UTC en vez de como hora local del navegador.
   */
  private parseDate(value: string): Date {
    const hasTimezone = RelativeTimePipe.TIMEZONE_PATTERN.test(value);
    return new Date(hasTimezone ? value : `${value}Z`);
  }
}
